/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useRef, useState } from 'react';
import { X } from 'lucide-react';
import { AHUControlProvider } from '@/contexts/ahu-control-context';
import { PowerControl } from './control/power-control';
import { OperationControl } from './control/operation-control';
import { SmallTemperatureControl } from './control/temperature-control';
import { ModeControl } from './control/mode-control';
import { FanControl } from './control/fan-control';
import { Button } from '../ui/button';
import { App, Select, SelectProps, Spin } from "antd";
import { Mode, OpMode, FanSpeed } from '@/types';
import { batchControlDevices } from '@/api/devices';
import { SUCCESS_MESSAGE } from '@/utils/constants';
import { useControlEnumValues } from '@/utils/controlUtils';
import { ManualControlParams } from '@/api/devices/types';

interface SelectedDevice {
  id: string;
}

interface MultiAHUControlModalProps {
  isOpen: boolean;
  onClose: () => void;
  selected: SelectedDevice[];
  options: SelectProps['options'] | undefined;
  onRefresh?: () => void;
  data: {
    status: 'on' | 'off' | 'alarm';
    setTemp: number;
    mode: Mode | null;
    opMode: OpMode;
    fanSpeed: FanSpeed;
  };
}

export function MultiAHUControlModal({ isOpen, onClose, data, options, selected, onRefresh }: MultiAHUControlModalProps) {
  const [selectedDevices, setSelectedDevices] = useState<string[]>(selected.map(device => device.id));
  const [mutationVars, setMutationVars] = useState(data);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { message } = App.useApp();
  const modalRef = useRef<HTMLDivElement>(null);
  const controlEnums = useControlEnumValues();

  // We'll remove the click outside handler since it's causing issues with the dropdown
  // The user can still close the modal using the X button

  if (!isOpen) return null;

  const handleSelectChange = (value: string[]) => {
    console.log('Select value changed:', value);
    setSelectedDevices(value);
  };

  const handlePowerChange = (value: boolean) => {
    setMutationVars(prev => ({
      ...prev,
      status: value ? 'on' : 'off'
    }));
  };

  // Operation is always Remote and disabled, so this handler doesn't need to do anything
  const handleOperationChange = (_: boolean) => {
    // No-op function - we don't want to send any commands for operation mode
    console.log('Operation mode change ignored - always using Remote');
    // We don't set opMode in mutationVars to avoid sending operation_mode_write commands
  };

  const handleTemperatureChange = (value: number) => {
    setMutationVars(prev => ({
      ...prev,
      setTemp: value
    }));
  };

  const handleModeChange = (value: Mode | null) => {
    setMutationVars(prev => ({
      ...prev,
      mode: value
    }));
  };

  const handleFanChange = (value: string) => {
    // Use the value directly as FanSpeed
    setMutationVars(prev => ({
      ...prev,
      fanSpeed: value as FanSpeed
    }));
  };

  const onOk = async () => {
    // Ensure devices are selected
    if (!selectedDevices || selectedDevices.length === 0) {
      message.open({
        type: 'warning',
        content: 'Please select at least one device to control.'
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const batchControls: ManualControlParams[] = [];

      // Log selected devices for debugging
      console.log('Selected devices for control:', selectedDevices);

      // Process each device
      for (const id of selectedDevices) {

        // Add power status
        if (mutationVars.status === 'on' || mutationVars.status === 'off') {
          batchControls.push({
            device_id: id,
            datapoint: 'status_write',
            value: controlEnums.getStatusValue(mutationVars.status)
          });
        }

        // We don't send operation_mode_write commands anymore
        // Operation is always Remote and disabled

        // Add temperature setpoint if mode is cool
        if (mutationVars.mode === 'cool' && mutationVars.setTemp) {
          batchControls.push({
            device_id: id,
            datapoint: 'temperature_setpoint_write',
            value: mutationVars.setTemp
          });
        }

        // Add mode
        if (mutationVars.mode) {
          batchControls.push({
            device_id: id,
            datapoint: 'mode_write',
            value: controlEnums.getModeValue(mutationVars.mode)
          });
        }

        // Add fan speed
        if (mutationVars.fanSpeed) {
          batchControls.push({
            device_id: id,
            datapoint: 'fan_mode_write',
            value: controlEnums.getFanSpeedValue(mutationVars.fanSpeed)
          });
        }
      }

      // Send batch control request if we have operations to perform
      if (batchControls.length > 0) {
        const response = await batchControlDevices(batchControls);

        // Log the response for debugging
        console.log('Batch control response:', response);

        if (response && response.results) {
          const successCount = response.results.filter(r => r.status === 'pending' || r.status === 'success').length;
          const errorCount = response.results.length - successCount;

          // Log the status breakdown for debugging
          console.log(`Control status: ${successCount} successful, ${errorCount} failed`);

          if (errorCount === 0) {
            message.open({
              type: 'success',
              content: SUCCESS_MESSAGE()
            });
          } else {
            message.open({
              type: 'warning',
              content: `${successCount} controls were scheduled successfully, but ${errorCount} failed.`
            });
          }

          // Refresh data to reflect changes in the UI
          if (successCount > 0 && onRefresh) {
            // Allow API changes to propagate before refreshing
            setTimeout(() => onRefresh(), 1500);
          }

          // Close modal on success if at least some operations succeeded
          if (successCount > 0) {
            setTimeout(() => onClose(), 1000);
          }
        }
      } else {
        message.open({
          type: 'info',
          content: 'No changes were made to apply.'
        });
      }
    } catch (e) {
      console.error('Batch control error:', e);
      message.open({
        type: 'error',
        content: `Failed to send controls: ${(e as Error).message}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Convert mode to the expected type for AHUControlProvider
  const controlData = {
    status: data.status,
    setTemp: data.setTemp,
    opMode: data.opMode,
    // Ensure mode is one of the allowed values or null
    mode: (data.mode === 'cool' || data.mode === 'fan' || data.mode === 'dry') ?
      (data.mode as 'cool' | 'fan' | 'dry') : 'cool',
    // Ensure fanSpeed is one of the allowed values or undefined
    fanSpeed: ['auto', 'low', 'med', 'high'].includes(data.fanSpeed as string) ?
      (data.fanSpeed as 'auto' | 'low' | 'med' | 'high') : 'auto'
  };

  return (
    <AHUControlProvider initialState={controlData}>
      <div
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center !mt-0"
        onClick={(e) => {
          // Only close if clicking directly on the backdrop, not on the modal content
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div
          ref={modalRef}
          className="w-[500px] bg-bg rounded-xl shadow-popup backdrop-blur-[20px]"
          onClick={(e) => e.stopPropagation()} // Prevent clicks from bubbling up
        >
          {/* Header */}
          <div className="px-4 pt-4 pb-3 flex justify-between items-center border-light-grey">
            <h2 className="text-dark-blue heading-5">AHU Control</h2>
            <button
              onClick={onClose}
              className="text-primary hover:text-dark-blue transition-colors"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex">
            <div className="flex-1 px-4 space-y-3">
              <h3 className="text-dark-blue heading-8">Selected Devices</h3>
              <div onClick={(e) => e.stopPropagation()}>
                <Select
                  className='w-full'
                  mode="multiple"
                  placeholder="Please Select"
                  maxTagCount="responsive"
                  value={selectedDevices}
                  onChange={handleSelectChange}
                  options={options}
                  optionFilterProp="label"
                  optionLabelProp="label"
                  style={{ fontSize: '12px' }}
                  listHeight={250}
                  dropdownStyle={{ zIndex: 9999 }}
                />
              </div>
              <h3 className="text-dark-blue heading-8">Control</h3>
              {/* Control Panel */}
              <div className="p-2.5 bg-white rounded-lg border border-light-grey shadow-sm">
                {/* Power & Operation */}
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <PowerControl onChange={handlePowerChange} />
                  <OperationControl onChange={handleOperationChange} />
                  <SmallTemperatureControl onChange={handleTemperatureChange} />
                </div>

                {/* Mode Selection */}
                <div className="grid grid-cols-2 gap-2">
                  <ModeControl ahuMode={data?.mode} onChange={handleModeChange} />
                  <FanControl ahuFan={data?.fanSpeed} onChange={handleFanChange} />
                </div>
              </div>
            </div>
          </div>
          <div className="flex px-4 py-2 justify-end">
            <Button
              size='sm'
              className="bg-primary text-white button-m h-[32px]"
              onClick={onOk}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <div className="flex gap-0.5">
                  <div className="w-1 h-1 bg-white rounded-full animate-loading-dots"></div>
                  <div className="w-1 h-1 bg-white rounded-full animate-loading-dots delay-150"></div>
                  <div className="w-1 h-1 bg-white rounded-full animate-loading-dots delay-300"></div>
                </div>
              ) : 'Confirm'}
            </Button>
          </div>
        </div>
      </div>
    </AHUControlProvider>
  );
}